import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Share2, ArrowRight, Download, Lock } from "lucide-react";
import { insights, team } from "../mock";
import { useToast } from "../hooks/use-toast";
import useSeo from "../hooks/useSeo";
import useAuth from "../hooks/useAuth";
import PremiumGate from "../components/PremiumGate";
import { downloadPdf } from "../lib/downloadPdf";

const InsightDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const post = insights.find((i) => i.slug === slug);
  useSeo({
    title: post?.title,
    description: post?.excerpt,
    image: post?.image,
    type: "article",
    path: `/insights/${slug}`,
    keywords: post?.tags?.join(", "),
  });
  if (!post) return <Navigate to="/insights" replace />;
  const locked = post.premium && !isAuthenticated;
  const author = team.find((t) => t.slug === post.authorSlug);
  const others = insights.filter((i) => i.slug !== slug).slice(0, 2);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: "Link copied", description: "Share it anywhere." });
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <>
      <section className="bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <Link to="/insights" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to insights
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="inline-block bg-teal-500 text-white text-xs font-bold tracking-widest px-3 py-1 rounded-full">
              {post.category.toUpperCase()}
            </span>
            {post.premium && (
              <span className="inline-flex items-center gap-1 bg-amber-500 text-black text-xs font-bold tracking-widest px-3 py-1 rounded-full">
                <Lock className="w-3 h-3" /> PREMIUM
              </span>
            )}
          </div>
          <h1 className="mt-4 text-3xl md:text-5xl font-extrabold leading-tight">{post.title}</h1>
          <div className="mt-5 flex items-center gap-5 text-sm text-neutral-300">
            <span>{post.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
            <button onClick={share} className="ml-auto inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 font-semibold">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </div>
      </section>

      {locked && (
        <PremiumGate kind="insight" title={post.title} excerpt={post.excerpt} image={post.image} />
      )}
      {!locked && (
        <>
          <article className="bg-white py-14 print-article">
            <div className="max-w-3xl mx-auto px-6">
              <img src={post.image} alt={post.title} className="w-full h-72 md:h-96 object-cover rounded-2xl shadow-lg" />

              <div className="mt-6 flex justify-end no-print">
                <button
                  onClick={async () => {
                    try {
                      await downloadPdf({
                        title: post.title,
                        kicker: post.category,
                        meta: `${post.date} · ${post.readTime} · ${post.author}`,
                        blocks: post.content,
                      });
                    } catch (e) {
                      toast({ title: "Download failed", description: "Please sign in again and retry." });
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {post.content.map((block, i) => {
                  if (block.type === "h") return <h2 key={i} className="text-2xl font-bold text-neutral-900 mt-4">{block.text}</h2>;
                  if (block.type === "quote") return (
                    <blockquote key={i} className="border-l-4 border-teal-500 pl-5 text-lg text-neutral-800 italic">&ldquo;{block.text}&rdquo;</blockquote>
                  );
                  if (block.type === "list") return (
                    <ul key={i} className="space-y-2 list-disc pl-5 text-neutral-700 leading-relaxed">
                      {block.items.map((it, j) => <li key={j}>{it}</li>)}
                    </ul>
                  );
                  return <p key={i} className="text-neutral-700 leading-relaxed text-base">{block.text}</p>;
                })}
              </div>

              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100">#{t}</span>
                ))}
              </div>

              {author && (
                <div className="mt-12 bg-neutral-50 rounded-2xl p-6 border border-neutral-100 flex items-center gap-5 no-print">
                  <img src={author.image} alt={author.role} className="w-16 h-16 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="text-xs font-bold text-teal-600 tracking-widest">AUTHOR</div>
                    <div className="font-bold text-neutral-900">{author.role}</div>
                    <div className="text-sm text-neutral-600">{author.department}</div>
                  </div>
                  <Link to={`/team/${author.slug}`} className="text-sm font-semibold text-teal-600 hover:gap-2 inline-flex items-center gap-1 transition-all">
                    View profile <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </article>

          {others.length > 0 && (
            <section className="bg-neutral-50 py-14 no-print">
              <div className="max-w-5xl mx-auto px-6">
                <h3 className="text-2xl font-bold text-neutral-900">Continue reading</h3>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                  {others.map((p) => (
                    <Link key={p.slug} to={`/insights/${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-lg transition-shadow flex">
                      <img src={p.image} alt={p.title} className="w-32 h-32 object-cover" />
                      <div className="p-4 flex-1">
                        <p className="text-xs font-bold text-teal-600 tracking-widest">{p.category.toUpperCase()}</p>
                        <h4 className="mt-1 font-semibold text-neutral-900 leading-snug group-hover:text-teal-600 transition-colors">{p.title}</h4>
                        <p className="mt-1 text-xs text-neutral-500">{p.date} · {p.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
};

export default InsightDetail;
